import { Box, Button, CircularProgress, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { Property } from "csstype";
import type React from "react";
import { type CSSProperties, forwardRef, type MouseEventHandler, type ReactNode, useCallback } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { ALPHA, BADGE, CONTROL_HEIGHT, DISABLED_CONTROL, FONT_SIZE, INK, INK_BLACK, NEUTRAL, SURFACE } from "@/theme/tokens";
import { TOUCH_TARGET_SX } from "../touchTarget";

/**
 * `hairline` is the calm outlined button: the `secondary` shape and ink on a light neutral outline (`NEUTRAL[200]`, the
 * resting outline `FilterPillButton` uses) instead of the dark one. It is for a way out that has to read as a button
 * without competing with the filled commit beside it, such as a sheet's Close or Back.
 */
type ButtonVariant = "primary" | "secondary" | "hairline" | "borderless" | "delete";

/** A semantic tint for a `secondary` button: a choice that carries a meaning (remove / maybe / advance). */
export type SystemButtonTone = "error" | "warning" | "success";

interface VariantColors {
	bg: string;
	bgHover: string;
	fg: string;
	border: Property.Border;
}

/** A toned secondary button: the tone's soft wash, its dark ink and a hairline in its main colour. */
const toneColors = (theme: Theme, tone: SystemButtonTone): VariantColors => ({
	bg: alpha(theme.palette[tone].main, ALPHA.HINT_TINT),
	bgHover: alpha(theme.palette[tone].main, ALPHA.HINT_TINT * 2),
	fg: theme.palette[tone].dark,
	border: `1px solid ${theme.palette[tone].main}`,
});

const getVariantColors = (theme: Theme, variant: ButtonVariant, tone: SystemButtonTone | undefined): VariantColors => {
	if (tone !== undefined && variant === "secondary") return toneColors(theme, tone);
	switch (variant) {
		case "primary":
			return {
				bg: theme.palette.primary.main,
				bgHover: theme.palette.primary.dark,
				fg: theme.palette.primary.contrastText,
				border: "none",
			};
		case "secondary":
			return {
				bg: SURFACE,
				bgHover: NEUTRAL[100],
				fg: INK.SECONDARY,
				border: `1px solid ${INK.SECONDARY}`,
			};
		case "hairline":
			return {
				bg: SURFACE,
				bgHover: NEUTRAL[100],
				fg: INK.SECONDARY,
				border: `1px solid ${NEUTRAL[200]}`,
			};
		case "borderless":
			return {
				bg: SURFACE,
				bgHover: NEUTRAL[100],
				fg: INK.SECONDARY,
				border: "none",
			};
		case "delete":
			return {
				bg: alpha(theme.palette.error.main, 1 - ALPHA.HOVER_OVERLAY * 4),
				bgHover: theme.palette.error.main,
				fg: theme.palette.error.contrastText,
				border: "none",
			};
	}
};

const variantToFontWeight: Record<ButtonVariant, CSSProperties["fontWeight"]> = {
	primary: 600,
	secondary: 500,
	hairline: 500,
	borderless: 500,
	delete: 600,
};

const variantToPadding: Record<ButtonVariant, CSSProperties["padding"]> = {
	primary: "10px 20px",
	secondary: "10px 20px",
	hairline: "10px 20px",
	borderless: "10px 10px",
	delete: "10px 20px",
};

const sizeToHeight: Record<string, CSSProperties["height"]> = {
	compact: CONTROL_HEIGHT.COMPACT,
	small: CONTROL_HEIGHT.STANDARD,
	medium: CONTROL_HEIGHT.LARGE,
	large: CONTROL_HEIGHT.XL,
};

/**
 * `compact` reads a notch smaller than the standard field height: 13px semibold in a 32px box, for a
 * dense action cluster inside a list row or a panel header, where a 40px/16px button is the loudest
 * thing on the surface. Everything else keeps the 16px label it has always had.
 */
const sizeToFontSize: Record<string, CSSProperties["fontSize"]> = {
	compact: FONT_SIZE.SECONDARY,
	small: "16px",
	medium: "16px",
	large: "16px",
};

/** `compact` also tightens the horizontal padding — a 32px box with 20px sides reads as a pill. */
const compactPadding: Record<ButtonVariant, CSSProperties["padding"]> = {
	primary: "6px 12px",
	secondary: "6px 12px",
	hairline: "6px 12px",
	borderless: "6px 8px",
	delete: "6px 12px",
};

const sizeToHeightLoading: Record<string, CSSProperties["height"]> = {
	compact: "12px",
	small: "12px",
	medium: "20px",
	large: "28px",
};

// Renders SystemButton as a compact 32px icon-only button (see the `iconButton` prop). Merged AFTER the
// variant base but BEFORE the caller's `sx`, so callers can still override — e.g. a red delete hover. The
// default hover uses the theme action token rather than a hardcoded grey.
const iconButtonSx: SxProps<Theme> = {
	minWidth: CONTROL_HEIGHT.COMPACT,
	width: CONTROL_HEIGHT.COMPACT,
	height: CONTROL_HEIGHT.COMPACT,
	padding: 0,
	color: INK.SECONDARY,
	backgroundColor: "transparent",
	"&:hover": { backgroundColor: "action.hover", boxShadow: "none" },
	"&.Mui-disabled": { backgroundColor: "transparent" },
};

type Props = {
	buttonVariant?: ButtonVariant;
	/** Tints a `secondary` button in a semantic colour (ignored on other variants). Omit for the neutral look. */
	tone?: SystemButtonTone;
	disabled?: boolean;
	children?: ReactNode;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	sx?: SxProps<Theme>;
	noFullWidthMobile?: boolean;
	/** Grow the hit area to `TOUCH_TARGET_MIN` on coarse pointers. For controls a finger actually aims at. */
	touchTarget?: boolean;
	/**
	 * Paint the disabled state on a neutral fill (`DISABLED_CONTROL`) instead of the variant's own. By default a
	 * disabled primary or delete button keeps its brand / red fill and only greys its label, which at a glance still
	 * reads as a live button — fine beside a form that explains itself, wrong for the one confirm at the bottom of a
	 * sheet. Off by default so every existing button keeps its look.
	 */
	mutedWhenDisabled?: boolean;
	mobileFullWidthPercentage?: string;
	size?: "compact" | "small" | "medium" | "large";
	/** Render as a compact 32px icon-only button (no label). Layer a custom hover via `sx` if needed. */
	iconButton?: boolean;
	leadingIcon?: ReactNode;
	loading?: boolean;
	showSoonBadge?: boolean;
	leadingIconGap?: CSSProperties["gap"];
	tooltip?: ReactNode;
	"data-testid"?: string;
	/**
	 * Annie walkthrough anchor (`context/standards/frontend.md`). Explicit for the same reason the a11y
	 * passthrough below is: SystemButton doesn't spread rest props, and TypeScript exempts hyphenated JSX
	 * attributes from excess-property checking — so an unknown `data-*` prop is dropped silently rather
	 * than failing the build, and the walkthrough step would never render.
	 */
	"data-annie-target"?: string;
	/** Marks this button as the control a walkthrough step's Next presses to reveal the next step, when the
	 * step's anchor is a container rather than the control. Declared for the same reason as the two above. */
	"data-walkthrough-reveal"?: string;
	showBadge?: string;
	type?: "button" | "submit" | "reset";
	/**
	 * Take the button OUT of the tab order with `-1` — for an action inside a composite control that is
	 * itself the single tab stop (a grid cell's reveal-on-hover action row), where the container owns the
	 * keyboard path instead. Only ever `-1` in practice; a positive value would fight the document order.
	 */
	tabIndex?: number;
	// Popover-trigger a11y passthrough (SystemButton doesn't spread rest props, so these must be explicit).
	"aria-expanded"?: React.AriaAttributes["aria-expanded"];
	/** The id of the region this button shows or hides — pair with `aria-expanded` on a disclosure toggle. */
	"aria-controls"?: string;
	"aria-haspopup"?: React.AriaAttributes["aria-haspopup"];
	"aria-label"?: string;
	/** ID of persistent guidance explaining this control. */
	"aria-describedby"?: string;
	"aria-pressed"?: React.AriaAttributes["aria-pressed"];
};

/**
 * SystemButton is a reusable button component that can be used throughout the application.
 */
const SystemButton = forwardRef<HTMLButtonElement, Props>(
	(
		{
			children,
			onClick,
			disabled,
			sx,
			noFullWidthMobile,
			touchTarget,
			mutedWhenDisabled,
			mobileFullWidthPercentage = "85%",
			size = "small",
			buttonVariant = "primary",
			tone,
			iconButton,
			leadingIcon,
			loading,
			showBadge,
			showSoonBadge,
			leadingIconGap = "8px",
			tooltip,
			type,
			tabIndex,
			"data-testid": dataTestId,
			"data-annie-target": annieTarget,
			"data-walkthrough-reveal": walkthroughReveal,
			"aria-expanded": ariaExpanded,
			"aria-controls": ariaControls,
			"aria-haspopup": ariaHasPopup,
			"aria-label": ariaLabel,
			"aria-describedby": ariaDescribedBy,
			"aria-pressed": ariaPressed,
		},
		ref,
	) => {
		const handleClick = useCallback(
			(event: React.MouseEvent<HTMLButtonElement>) => {
				if (loading) {
					event.preventDefault();
					event.stopPropagation();
					return;
				}
				onClick?.(event);
			},
			[loading, onClick],
		);

		const buttonElement = (
			<Button
				type={type}
				tabIndex={tabIndex}
				data-testid={dataTestId}
				data-annie-target={annieTarget}
				data-walkthrough-reveal={walkthroughReveal}
				aria-expanded={ariaExpanded}
				aria-controls={ariaControls}
				aria-haspopup={ariaHasPopup}
				aria-label={ariaLabel}
				aria-describedby={ariaDescribedBy}
				aria-pressed={ariaPressed}
				ref={ref}
				// MUI's own union is small|medium|large, and `compact` is OUR step below small — its height,
				// padding and label size all come from the `sx` below, so MUI only needs the nearest size.
				size={size === "compact" ? "small" : size}
				onClick={handleClick}
				variant="contained"
				disabled={disabled}
				sx={[
					(theme) => {
						const colors = getVariantColors(theme, buttonVariant, tone);
						return {
							boxShadow: "none",
							backgroundColor: colors.bg,
							color: colors.fg,
							borderRadius: "8px",
							textTransform: "none",
							fontWeight: variantToFontWeight[buttonVariant],
							fontSize: sizeToFontSize[size],
							border: colors.border,
							"&:hover": {
								backgroundColor: colors.bgHover,
								boxShadow: "none",
							},
							// Override MUI's default contained-disabled grey fill for borderless/secondary buttons.
							"&.Mui-disabled": mutedWhenDisabled
								? {
										backgroundColor: buttonVariant === "borderless" ? "transparent" : DISABLED_CONTROL.FILL,
										color: DISABLED_CONTROL.INK,
										border: buttonVariant === "secondary" || buttonVariant === "hairline" ? `1px solid ${DISABLED_CONTROL.BORDER}` : colors.border,
									}
								: {
										backgroundColor: buttonVariant === "borderless" ? "transparent" : colors.bg,
										color: INK.DISABLED,
										border: colors.border,
									},
							// Omitted, not overridden, for icon buttons: the responsive form compiles to an
							// `@media (min-width:0px)` block that outranks the plain `width` set later by
							// `iconButtonSx`/caller `sx`, so a 24px icon button rendered ~85% wide on a phone.
							...(iconButton
								? {}
								: {
										width: {
											xs: noFullWidthMobile ? "fit-content" : mobileFullWidthPercentage,
											sm: "fit-content",
										},
										minWidth: "fit-content",
									}),
							padding: size === "compact" ? compactPadding[buttonVariant] : variantToPadding[buttonVariant],
							height: sizeToHeight[size],
							whiteSpace: "noWrap",
							position: "relative",
						};
					},
					...(iconButton ? [iconButtonSx] : []),
					...(Array.isArray(sx) ? sx : [sx]),
					// LAST on purpose. Opt-in only: `min-height` clamps `height`, so applying this to every
					// button would break the 40px row-alignment invariant on every tablet
					// (CONTROL_HEIGHT.STANDARD). It has to outrank `iconButtonSx` and the caller's own `sx` —
					// a compact icon button passes `minWidth: 24px`, and whichever declaration Emotion happens
					// to serialize later would otherwise decide the hit area.
					...(touchTarget ? [TOUCH_TARGET_SX] : []),
				]}
			>
				{showBadge && (
					<Box
						sx={(theme) => ({
							position: "absolute",
							top: "-10px",
							right: "-8px",
							backgroundColor: theme.palette.error.main,
							color: theme.palette.error.contrastText,
							fontSize: "10px",
							fontWeight: "bold",
							padding: "2px 6px",
							borderRadius: BADGE.PILL_RADIUS_FULL,
							textTransform: "uppercase",
							lineHeight: "12px",
							minWidth: "20px",
							textAlign: "center",
							boxShadow: `0 2px 4px ${alpha(INK_BLACK, ALPHA.SHADOW_MEDIUM)}`,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						})}
					>
						{showBadge}
					</Box>
				)}
				{showSoonBadge && (
					<Box
						sx={(theme) => ({
							position: "absolute",
							top: "-10px",
							left: "-8px",
							backgroundColor: theme.palette.error.main,
							color: theme.palette.error.contrastText,
							fontSize: "10px",
							fontWeight: "bold",
							padding: "2px 6px",
							borderRadius: BADGE.PILL_RADIUS_FULL,
							textTransform: "uppercase",
							lineHeight: "12px",
							minWidth: "20px",
							textAlign: "center",
							boxShadow: `0 2px 4px ${alpha(INK_BLACK, ALPHA.SHADOW_MEDIUM)}`,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						})}
					>
						SOON
					</Box>
				)}
				{!(loading && iconButton) &&
					(leadingIcon ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: leadingIconGap,
							}}
						>
							{leadingIcon}
							{children}
						</Box>
					) : (
						children
					))}

				{loading && (
					<Box>
						<CircularProgress
							size={sizeToHeightLoading[size]}
							sx={(theme) => ({
								ml: iconButton ? 0 : 1,
								color: `${getVariantColors(theme, buttonVariant, tone).fg} !important`,
							})}
						/>
					</Box>
				)}
			</Button>
		);
		return tooltip ? (
			<InHouseTooltip title={tooltip}>
				<Box>{buttonElement}</Box>
			</InHouseTooltip>
		) : (
			buttonElement
		);
	},
);

SystemButton.displayName = "SystemButton";

export default SystemButton;
